import React, {
    useState,
    forwardRef,
    useImperativeHandle,
    useEffect,
    useRef,
    useContext,
} from "react"

import ExpandableComponentContext from "../../Contexts/ExpandableComponentContext"

const MonthYearElement = forwardRef(({ title, data, readOnly = false, maxFutureYearsFromNow = 10 }, ref) => {

    const ExpandableComponentData = useContext(ExpandableComponentContext)

    const isInteracted = ExpandableComponentData ? ExpandableComponentData?.isInteracted : false

    const [month, setMonth] = useState(data?.month ?? "")
    const [year, setYear] = useState(data?.year ? String(data.year) : "")
    const [error, setError] = useState(false)

    const isFirstTimeRef = useRef(true)

    const months = Array.from({ length: 12 }, (_, i) => {
        const monthNum = (i + 1).toString().padStart(2, "0")
        return monthNum
    })


    const currentYear = new Date().getFullYear()
    const startYear = 1900
    const years = []
    for (let y = currentYear + maxFutureYearsFromNow; y >= startYear; y--) {
        years.push(String(y))
    }

    useEffect(() => {
        if (isFirstTimeRef.current) {
            isFirstTimeRef.current = false
            return
        }

        setMonth(data?.month ?? "")
        setYear(data?.year ? String(data.year) : "")
    }, [data])

    useImperativeHandle(ref, () => ({
        validateAndGetData: () => {
            const isMonthValid = months.includes(month)
            const isYearValid = years.includes(year)
            const isValid = isMonthValid && isYearValid

            if (isValid) {
                setError(false)
            }
            else {
                if (isInteracted) {
                    setError(true)
                }
            }

            return {
                isValid,
                value: {
                    month,
                    year,
                },
                isInteracted,
            }
        },
    }))

    return (
        <div className="py-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">{title}</label>
            <div className="flex space-x-4">
                <div className="relative w-32">
                    <select
                        disabled={readOnly}
                        value={month}
                        onChange={(e) => {
                            setMonth(e.target.value)
                            setError(false)
                        }}
                        className={`block w-full appearance-none rounded-md border px-3 py-2 pr-8 text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${error && !month ? "border-red-500" : "border-gray-300"
                            }`}
                    >
                        <option value="">Month</option>
                        {months.map((m) => (
                            <option key={m} value={m}>
                                {m}
                            </option>
                        ))}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                        <svg
                            className="h-4 w-4"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7"></path>
                        </svg>
                    </div>
                </div>

                {/* Year selector */}
                <div className="relative w-32">
                    <select
                        disabled={readOnly}
                        value={year}
                        onChange={(e) => {
                            setYear(e.target.value)
                            setError(false)
                        }}
                        className={`block w-full appearance-none rounded-md border px-3 py-2 pr-8 text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${error && !year ? "border-red-500" : "border-gray-300"
                            }`}
                    >
                        <option value="">Year</option>
                        {years.map((y) => (
                            <option key={y} value={y}>
                                {y}
                            </option>
                        ))}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                        <svg
                            className="h-4 w-4"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7"></path>
                        </svg>
                    </div>
                </div>
            </div>
            {error && (
                <p className="mt-1 text-sm text-red-600">Please select a valid month and year.</p>
            )}
        </div>
    )
})

export default MonthYearElement
